import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import GenericButton, { ButtonVariant } from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import ReflectorSelector from "../shared/ReflectorSelector";
import {
  SystemAction,
  SystemStatusResult,
  fetchSystemStatus,
  getDefaultServices,
  sendServiceRestart,
  sendServiceStop,
} from "./request";

const ACTION_LABELS: Record<SystemAction, string> = {
  status: "Status",
  stop: "Stop",
  restart: "Restart",
};

const ACTION_VARIANTS: Record<SystemAction, ButtonVariant> = {
  status: "brand",
  stop: "danger",
  restart: "secondary",
};

export default function SystemStatusPage() {
  const { getAccessTokenSilently } = useAuth0();

  const [ctxrUuid, setCtxrUuid] = useState("");
  const [customServiceInput, setCustomServiceInput] = useState("");
  const [defaultServices, setDefaultServices] = useState<string[]>([]);
  const [selectedServices, setSelectedServices] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<SystemAction | null>(null);
  const [result, setResult] = useState<SystemStatusResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getAccessTokenSilently()
      .then((token) => getDefaultServices(token))
      .then((svcs) => {
        setDefaultServices(svcs);
        setSelectedServices(new Set(svcs));
      })
      .catch(() => {
        // use a fallback list if backend unreachable
      });
  }, [getAccessTokenSilently]);

  const toggleService = useCallback((svc: string) => {
    setSelectedServices((prev) => {
      const next = new Set(prev);
      if (next.has(svc)) {
        next.delete(svc);
      } else {
        next.add(svc);
      }
      return next;
    });
  }, []);

  const addCustomService = useCallback(() => {
    const trimmed = customServiceInput.trim();
    if (!trimmed) return;
    setDefaultServices((prev) =>
      prev.includes(trimmed) ? prev : [...prev, trimmed]
    );
    setSelectedServices((prev) => {
      const next = new Set(prev);
      next.add(trimmed);
      return next;
    });
    setCustomServiceInput("");
  }, [customServiceInput]);

  const runAction = useCallback(
    async (action: SystemAction) => {
      if (!ctxrUuid.trim()) {
        setError("Reflector UUID is required.");
        return;
      }
      const services = selectedServices.size > 0 ? Array.from(selectedServices) : undefined;
      setLoading(action);
      setError(null);
      setResult(null);

      try {
        const token = await getAccessTokenSilently();
        const target = { ctxrUuid: ctxrUuid.trim(), services };
        let res: SystemStatusResult;
        if (action === "status") {
          res = await fetchSystemStatus(target, token);
        } else if (action === "stop") {
          res = await sendServiceStop(target, token);
        } else {
          res = await sendServiceRestart(target, token);
        }
        setResult(res);
      } catch (err: unknown) {
        const msg =
          err instanceof Error
            ? err.message
            : "Unexpected error. Check the reflector is online.";
        setError(msg);
      } finally {
        setLoading(null);
      }
    },
    [ctxrUuid, selectedServices, getAccessTokenSilently]
  );

  const exitCodeBadge = (code: number) => {
    const ok = code === 0;
    return (
      <span
        style={{
          display: "inline-block",
          padding: "2px 8px",
          borderRadius: 4,
          fontSize: 12,
          fontWeight: 600,
          background: ok ? "#d1fae5" : "#fee2e2",
          color: ok ? "#065f46" : "#991b1b",
          marginLeft: 8,
        }}
      >
        exit {code}
      </span>
    );
  };

  return (
    <div style={{ padding: "24px", maxWidth: 960 }}>
      <h2 style={{ marginBottom: 4 }}>System Service Management</h2>
      <p style={{ color: "#6b7280", marginBottom: 24 }}>
        Run on-demand <code>systemctl</code> commands on a reflector via
        WebSocket. Results are collected asynchronously.
      </p>

      {/* Target reflector */}
      <GenericCard isCollapsible={false} title="Target Reflector">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label htmlFor="sys-ctxr-uuid" style={{ fontWeight: 500, fontSize: 14 }}>
            Reflector
          </label>
          <ReflectorSelector
            id="sys-ctxr-uuid"
            value={ctxrUuid}
            onChange={setCtxrUuid}
            disabled={!!loading}
          />
        </div>
      </GenericCard>

      {/* Service selection */}
      <GenericCard isCollapsible={false} title="Services" style={{ marginTop: 16 }}>
        <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>
          Toggle services to include. Add custom service names as needed.
        </p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
          {defaultServices.map((svc) => (
            <button
              key={svc}
              type="button"
              onClick={() => toggleService(svc)}
              style={{
                padding: "4px 12px",
                borderRadius: 20,
                border: "1px solid",
                cursor: "pointer",
                fontSize: 13,
                fontFamily: "monospace",
                background: selectedServices.has(svc) ? "#2563eb" : "#f3f4f6",
                borderColor: selectedServices.has(svc) ? "#2563eb" : "#d1d5db",
                color: selectedServices.has(svc) ? "#fff" : "#374151",
              }}
            >
              {svc}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <input
            id="sys-custom-service"
            type="text"
            value={customServiceInput}
            onChange={(e) => setCustomServiceInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") addCustomService();
            }}
            placeholder="custom-service.service"
            style={{
              flex: 1,
              padding: "6px 10px",
              border: "1px solid #d1d5db",
              borderRadius: 6,
              fontFamily: "monospace",
              fontSize: 13,
            }}
          />
          <GenericButton
            label="Add"
            variant="secondary"
            onClick={addCustomService}
            disabled={!customServiceInput.trim()}
          />
        </div>
      </GenericCard>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
        {(["status", "stop", "restart"] as SystemAction[]).map((action) => (
          <GenericButton
            key={action}
            label={
              loading === action
                ? `Running ${ACTION_LABELS[action]}…`
                : ACTION_LABELS[action]
            }
            variant={ACTION_VARIANTS[action]}
            onClick={() => runAction(action)}
            disabled={!!loading || !ctxrUuid.trim()}
          />
        ))}
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            marginTop: 20,
            padding: "12px 16px",
            background: "#fef2f2",
            border: "1px solid #fca5a5",
            borderRadius: 8,
            color: "#b91c1c",
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <GenericCard
          isCollapsible={false}
          title={`Result — ${ACTION_LABELS[result.action]} (${result.respondedAt ? new Date(result.respondedAt).toLocaleTimeString() : ""})`}
          style={{ marginTop: 20 }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {Object.entries(result.output).map(([svc, res]) => (
              <div
                key={svc}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    background: "#f9fafb",
                    padding: "8px 14px",
                    borderBottom: "1px solid #e5e7eb",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={{ fontFamily: "monospace", fontWeight: 600, fontSize: 14 }}>
                    {svc}
                  </span>
                  {exitCodeBadge(res.exit_code)}
                </div>
                <pre
                  style={{
                    margin: 0,
                    padding: "12px 14px",
                    background: "#1e1e1e",
                    color: "#d4d4d4",
                    fontSize: 12,
                    fontFamily: "monospace",
                    overflowX: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 320,
                    overflowY: "auto",
                  }}
                >
                  {res.output || res.error || "(no output)"}
                </pre>
              </div>
            ))}
          </div>
        </GenericCard>
      )}
    </div>
  );
}
